import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { apiRequest } from '../../../utils/fetch';
import { AdSpaceOwnerPackageInterface, GenericResponseInterface } from '../../../interfaces/interfaces';

const initialPackage: Partial<AdSpaceOwnerPackageInterface> = {
  packageTotalPrice: 0,
  userId: 0,
  notes: '',
  adSpacesInThisPackage: [],
  active: true,
};

const AdSpaceOwnerPackagesAdminAddOwnerPackagePage: React.FC = () => {
  const [pkg, setPkg] = useState<Partial<AdSpaceOwnerPackageInterface>>(initialPackage);
  const [adSpacesInput, setAdSpacesInput] = useState('[]');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleChange = (field: keyof AdSpaceOwnerPackageInterface, value: any) => {
    setPkg(a => ({ ...a, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    let adSpacesArray: any[] = [];
    try {
      const arr = JSON.parse(adSpacesInput);
      if (Array.isArray(arr)) {
        adSpacesArray = arr;
      } else {
        setError('Ad Space IDs must be a JSON array, e.g. [1, 2, 3]');
        return;
      }
    } catch {
      setError('Ad Space IDs must be a valid JSON array, e.g. [1, 2, 3]');
      return;
    }

    try {
      const result = await apiRequest<GenericResponseInterface<AdSpaceOwnerPackageInterface>>('/adSpaceOwnerPackages/insert', 'POST', {
        ...pkg,
        adSpacesInThisPackage: adSpacesArray,
      });
      if (result.status) {
        setSuccess(result.message || 'Owner package created successfully!');
        setDialogOpen(true);
        setPkg(initialPackage);
        setAdSpacesInput('[]');
      } else {
        setError(result.message || 'Failed to create owner package');
        setDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create owner package');
      setDialogOpen(true);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add New Ad Space Owner Package</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
          label="Total Price"
          type="number"
          value={pkg.packageTotalPrice ?? ''}
          onChange={e => handleChange('packageTotalPrice', Number(e.target.value))}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="User ID"
          type="number"
          value={pkg.userId ?? ''}
          onChange={e => handleChange('userId', Number(e.target.value))}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="Notes"
          value={pkg.notes ?? ''}
          onChange={e => handleChange('notes', e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Ad Space IDs in this package (JSON array)"
          value={adSpacesInput}
          onChange={e => setAdSpacesInput(e.target.value)}
          fullWidth
          margin="dense"
          helperText='Enter a JSON array with user adSpace IDs, e.g. [1, 2, 3]'
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="active-label">Active</InputLabel>
          <Select
            labelId="active-label"
            value={pkg.active !== undefined ? (pkg.active ? 'yes' : 'no') : 'yes'}
            label="Active"
            onChange={e => handleChange('active', e.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create Owner Package
          </Button>
        </Box>
      </form>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{success || error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdSpaceOwnerPackagesAdminAddOwnerPackagePage;